import moment from 'moment';
import {toDateOnly} from '../../../../../utility/dateUtil';
import {validateNINumber} from '../../../../../utility/validationUtil';

export const MAX_FSM_CONTACTS = 5;

/**
 * Calendar date of birth for FSM eligibility contacts (AD-2588).
 * Moments use the picker's calendar day as UTC midnight; ISO/API values use the
 * Europe/London calendar day so legacy local-midnight→UTC shifts still hydrate
 * as the day staff saw on the profile.
 */
export const toFsmContactDateOnly = (value) => {
    if (value == null || value === '') {
        return null;
    }

    if (moment.isMoment(value)) {
        return toDateOnly(value);
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return null;
    }

    const parts = new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Europe/London',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).formatToParts(date);

    const year = Number(parts.find((p) => p.type === 'year')?.value);
    const month = Number(parts.find((p) => p.type === 'month')?.value);
    const day = Number(parts.find((p) => p.type === 'day')?.value);
    if (!year || !month || !day) {
        return null;
    }

    return moment.utc([year, month - 1, day]).startOf('day').toISOString();
};

export const fsmContactDobCalendarDay = (value) => {
    const iso = toFsmContactDateOnly(value);
    return iso ? moment.utc(iso).format('YYYY-MM-DD') : '';
};

export const isContactComplete = (contact) => {
    if (!contact) {
        return false;
    }

    const niValidation = validateNINumber(contact.insuranceNumber);
    return !!contact.fullName &&
        !!contact.dateOfBirth &&
        !!contact.insuranceNumber &&
        niValidation.isValid &&
        !contact.fullNameError &&
        !contact.dateOfBirthError &&
        !contact.insuranceNumberError;
};

// AD-2646: whether a parent who chooses "currently in receipt" is asked for their details. The server works it out
// for each child (an applicant's school setting, or off for a child on roll) and sends 0/'Off', 1/'Optional' or
// 2/'Required'.
export const FSM_IN_RECEIPT = {off: 'off', optional: 'optional', required: 'required'};

export const fsmInReceiptMode = (value) => {
    const text = String(value ?? '').toLowerCase();
    if (text === '1' || text === 'optional') return FSM_IN_RECEIPT.optional;
    if (text === '2' || text === 'required') return FSM_IN_RECEIPT.required;
    return FSM_IN_RECEIPT.off;
};

const isEligibilityCheck = ({lastFree, isNotApplicable}) => !!(lastFree || isNotApplicable);

const asksInReceipt = ({lastFree, isNotApplicable, currentlyFree, inReceipt}) =>
    !isEligibilityCheck({lastFree, isNotApplicable}) && !!currentlyFree &&
    fsmInReceiptMode(inReceipt) !== FSM_IN_RECEIPT.off;

const isContactBlank = (contact) =>
    !contact || (!contact.fullName && !contact.dateOfBirth && !contact.insuranceNumber);

/**
 * Parent contacts are shown and required for eligibility checks (consent-to-check / last 6 years). AD-2646: they are
 * also shown when currently in receipt, if the school asks applicants' parents for them.
 */
export const shouldShowFsmContacts = ({lastFree, isNotApplicable, currentlyFree, inReceipt}) =>
    isEligibilityCheck({lastFree, isNotApplicable}) ||
    asksInReceipt({lastFree, isNotApplicable, currentlyFree, inReceipt});

/** @deprecated Use shouldShowFsmContacts — same rule. */
export const requiresFsmContacts = shouldShowFsmContacts;

export const areFsmContactsValid = ({lastFree, isNotApplicable, contacts, currentlyFree, inReceipt}) => {
    const showContacts = shouldShowFsmContacts({lastFree, isNotApplicable, currentlyFree, inReceipt});
    if (!showContacts) {
        return true;
    }

    // AD-2646: when asked as optional, every row may be left blank; anything entered must still be complete.
    if (asksInReceipt({lastFree, isNotApplicable, currentlyFree, inReceipt}) &&
        fsmInReceiptMode(inReceipt) === FSM_IN_RECEIPT.optional) {
        const filled = (contacts ?? []).filter(contact => !isContactBlank(contact));
        return filled.length <= MAX_FSM_CONTACTS && filled.every(isContactComplete);
    }

    if (!Array.isArray(contacts) || contacts.length < 1 || contacts.length > MAX_FSM_CONTACTS) {
        return false;
    }

    return contacts.every(isContactComplete);
};

export const contactsPayloadForSave = ({lastFree, isNotApplicable, contacts, currentlyFree, inReceipt}) => {
    if (!shouldShowFsmContacts({lastFree, isNotApplicable, currentlyFree, inReceipt})) {
        return [];
    }

    // AD-2646: blank rows left with "currently in receipt" are not sent.
    const rows = asksInReceipt({lastFree, isNotApplicable, currentlyFree, inReceipt})
        ? contacts.filter(contact => !isContactBlank(contact))
        : contacts;

    return rows.map(({fullName, dateOfBirth, insuranceNumber}) => ({
        fullName,
        dateOfBirth: toFsmContactDateOnly(dateOfBirth),
        insuranceNumber,
    }));
};

export const normalizeFsmContactForCompare = ({fullName, dateOfBirth, insuranceNumber}) => ({
    fullName: fullName || '',
    dateOfBirth: fsmContactDobCalendarDay(dateOfBirth),
    insuranceNumber: insuranceNumber || '',
});
